<template>
    <ideo-form-group v-if="ableToAssigneUserOutsideCurrentDistrict" :label="$t('[[[Wybierz osobę spoza dzielnicy]]]')">
        <ideo-form-checkbox v-model="userOutsideCurrentDistricts" :switch="true" class="ml-3" />
    </ideo-form-group>
    <ideo-form-group :label="$t('[[[Wybierz użytkownika]]]')">
        <ideo-autocomplete
            v-model="selectedUserId"
            :name="name"
            :track-by="trackBy"
            :placeholder="placeholder || $t('[[[Zacznij pisać aby wyszukać użytkownika...]]]')"
            :fetch="(id: ModelValue) => handleFetchUser(id)"
            :search="(query: string, limit: number) => searchUser(query, limit, userDepartment, showAllRecords, propertyPublicId, null, userOutsideCurrentDistricts, cityPublicId, districtPublicId)"
            @update:modelValue="addUser()"
        >
            <template #selected="{ option }">
                <div class="d-flex flex-nowrap align-items-center">
                    <b class="ml-2">{{ option.givenName }} {{ option.surname }}</b>
                    <i class="ml-auto">{{ option.citiesNames }}</i>
                </div>
            </template>
            <template #option="{ option }">
                <div class="d-flex flex-nowrap align-items-center">
                    <b class="ml-2">{{ option.givenName }} {{ option.surname }}</b>
                    <i class="ml-auto">{{ option.citiesNames }}</i>
                </div>
            </template>
        </ideo-autocomplete>
    </ideo-form-group>
    <ideo-form-group :label="$t('[[[Przypisane do:]]]')">
        <div class="alert alert-dark" v-if="!(value && value.length)">
            {{ $t('[[[Brak przypisanych użytkowników.]]]') }}
        </div>
        <div v-else class="row">
            <div class="col">
                <ul class="list-group list-group-flush">
                    <li class="list-group-item bg-light px-0" v-for="(item) in selectedUsers" :key="item.id.toString()">
                        <div class="task-owner">
                            <i class="fas fa-user"></i>
                            <b>{{ item.givenName }} {{ item.surname }}</b>
                            <i class="ml-auto">{{ item.email }}</i>
                            <a class="btn px-0" @click="removeUser(item.id)">
                                <i class="fas fa-minus-circle"></i>
                            </a>
                        </div>
                    </li>
                </ul>
            </div>
        </div>
    </ideo-form-group>
    <ideo-form-group class="text-center">
        <action-button icon="fa fa-plus" :disabled="fetchingAllUsers" v-if="showAddAll" variant="primary" :text="$t('[[[Dodaj wszystkich dopasowanych użytkowników]]]')" @click="handleClickAddAllUsers" />
    </ideo-form-group>
</template>
<script lang="ts">
import { Vue, Options } from 'vue-class-component';
import { Prop, Watch } from '@/helpers/Decorators';
import { fetchUser, searchUser, fetchUserById } from '@/modules/renter/common/helpers/autocomplete';
import { DepartmentEnum } from '@/modules/core/users/services/UsersService';
import type { UserItemModel } from '@/modules/renter/tasks/services/TasksService';

type ModelValue = number | string;

@Options({
    emits: ['update:modelValue', 'onLoading']
})
export default class RenterAssignUser extends Vue
{
    @Prop() public modelValue: ModelValue[];
    @Prop({ default: '' }) public userDepartment: DepartmentEnum;
    @Prop({ default: 'publicId' }) public trackBy: 'publicId' | 'id';
    @Prop({ default: null }) public placeholder: string;
    @Prop({ default: false }) public forceId!: boolean;
    @Prop({ default: null }) public propertyPublicId: string | null;
    @Prop({ default: null }) public cityPublicId: string | null;
    @Prop({ default: null }) public districtPublicId: string | null;
    @Prop() public name: '';
    @Prop({ default: [] }) public initAssignedUsers: UserItemModel[];
    @Prop({ default: false }) public ableToAssigneUserOutsideCurrentDistrict: UserItemModel[];
    @Prop({ default: false }) public showAllRecords: boolean;
    @Prop({ default: false }) public showAddAll: boolean;

    public value: ModelValue[] = [];
    public userOutsideCurrentDistricts = false;

    public fetchingAllUsers = false;
    private firstChange = true;

    public async handleClickAddAllUsers(): Promise<void>
    {
        this.fetchingAllUsers = true;

        try
        {
            const users = await searchUser('', 100, this.userDepartment, this.showAllRecords, this.propertyPublicId, null, this.userOutsideCurrentDistricts, this.cityPublicId, this.districtPublicId);

            users.forEach( (user) =>
            {
                if (this.value.includes(user[this.forceId ? 'id' : this.trackBy])) return;

                this.selectedUsers.push(user);
                this.value.push(user[this.forceId ? 'id' : this.trackBy]);
                this.update();
            });
        }
        catch ( error )
        {
            this.handleException(error, {
                400: () =>
                {
                    this.$alert.error(this.$t('[[[Nie udało się pobrać użytkowników]]]'));
                }
            });
        }
        finally
        {
            this.fetchingAllUsers = false;
        }

    }

    public async mounted(): Promise<void>
    {
        this.value = this.modelValue;

        if (this.initAssignedUsers.length)
        {
            this.setDefaultUsers();
        }
        else
        {
            if (Array.isArray(this.modelValue))
            {
                this.selectedUsers = await Promise.all(this.modelValue.map(id => this.handleFetchUser(id)));
            }
            else
            {
                this.selectedUsers = [];
            }
        }
    }

    public selectedUsers: UserItemModel[] = [];
    public selectedUserId: ModelValue = null;

    public async addUser(): Promise<void>
    {
        if (this.selectedUserId === null) return;

        this.$emit('onLoading', true);

        const user = await this.handleFetchUser(this.selectedUserId);

        this.selectedUserId = null;

        if (this.value.includes(user[this.forceId ? 'id' : this.trackBy]))
        {
            this.$emit('onLoading', false);

            return;
        }

        this.selectedUsers.push(user);
        this.value.push(user[this.forceId ? 'id' : this.trackBy]);
        this.update();
        this.selectedUserId = null;
        this.$emit('onLoading', false);
    }

    public removeUser(id: number | string): void
    {
        const index = this.selectedUsers.findIndex((user: UserItemModel) => user[this.forceId ? 'id' : this.trackBy] === id);

        this.selectedUsers.splice(index, 1);
        this.value.splice(index, 1);
        this.update();
    }

    public update(): void
    {
        this.$emit('update:modelValue', this.value);
    }

    public handleFetchUser(id: number | string): Promise<UserItemModel>
    {
        if (this.trackBy === 'publicId')
            return fetchUser(id as string);
        else
            return fetchUserById(id as number);
    }

    public setDefaultUsers(): void
    {
        const getUsersList = this.initAssignedUsers.map(user => user.id);

        this.value = getUsersList;
        this.selectedUsers = this.initAssignedUsers;
        this.update();
    }
    @Watch('initAssignedUsers') private setDefaultAssignedUsers(): void
    {
        if (this.firstChange)
        {
            this.firstChange = false;

            return;
        }

        if (this.initAssignedUsers.length)
            this.setDefaultUsers();
    }

    @Watch('modelValue') private onModelValue(): void
    {
        this.value = this.modelValue;
        this.selectedUsers = this.selectedUsers.filter(user => this.modelValue.includes(user[this.forceId ? 'id' : this.trackBy]));
    }

    public searchUser = searchUser;
}
</script>

<style scoped>
    .task-owner {
        display: grid;
        grid-template-columns: 15px 1fr 1fr 15px;
        align-items: center;
        gap: 10px;
        width: 100%;
        word-wrap: anywhere;
    }
</style>
